import {
  Anchor,
  Menu,
  Text,
  Tooltip,
  UnstyledButton,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import {
  IconAlertTriangle,
  IconArrowUpRight,
  IconEyeEdit,
  IconExclamationMark,
  IconMessageReply,
} from '@tabler/icons-react';
import type { ActionRequest } from 'interfaces/actionRequest';
import type { CSSProperties, SyntheticEvent } from 'react';
import { Link } from 'react-router-dom';
import { QUERIES_WINDOW_TARGET } from 'utils/crossTabNavigation';
import {
  actionRequestNeedsResponse,
  ClaimActionBadge,
  getLatestVisibleActionRequest,
} from './ClaimActionBadge';
import { ClaimAlertMark } from './ClaimAlertMark';

interface ClaimAlertsProps {
  claimID: string;
  invalid: boolean;
  manualReview: boolean;
  queryResponse: boolean;
  canViewQueries: boolean;
  canEdit: boolean;
  actionManagementEnabled: boolean;
  actionRequests: ActionRequest[];
  isProvider: boolean;
  onOpenActions: () => void;
  onEdit: () => void;
}

type AlertType = 'invalid' | 'action' | 'review' | 'query';

export const ClaimAlerts = ({
  claimID,
  invalid,
  manualReview,
  queryResponse,
  canViewQueries,
  canEdit,
  actionManagementEnabled,
  actionRequests,
  isProvider,
  onOpenActions,
  onEdit,
}: ClaimAlertsProps) => {
  const { translate } = useTranslation();
  const colorScheme = useComputedColorScheme('dark');
  const visibleActionRequest = actionManagementEnabled
    ? getLatestVisibleActionRequest(actionRequests)
    : undefined;
  const needsResponse = visibleActionRequest
    ? actionRequestNeedsResponse(visibleActionRequest, isProvider)
    : false;
  const actionState = needsResponse ? 'respond' : 'sent';
  const role = isProvider ? 'provider' : 'biller';
  const actionLabel = translate(
    `pages:claims.actionBadge.${actionState}`,
    needsResponse ? 'Respond' : 'Sent'
  );
  const queryLabel = translate(
    'pages:claims.table.columns.queryResponseReceived',
    'Health authority query response received'
  );
  const reviewLabel = translate(
    'pages:claims.table.columns.manualReviewRequired',
    'Manual review required'
  );
  const alertTypes: AlertType[] = [];
  if (manualReview) alertTypes.push('review');
  if (invalid && !isProvider) alertTypes.push('invalid');
  if (visibleActionRequest) alertTypes.push('action');
  if (queryResponse) alertTypes.push('query');

  if (alertTypes.length === 0) return null;

  const labels: Record<AlertType, string> = {
    invalid: translate('pages:claims.alerts.invalid', 'Invalid data detected'),
    action: actionLabel,
    review: reviewLabel,
    query: queryLabel,
  };
  const descriptions: Record<AlertType, string> = {
    invalid: translate(
      'pages:claims.alerts.invalidDescription',
      'Review the flagged fields and save the claim.'
    ),
    action: translate(`pages:claims.actionBadge.${role}.${actionState}`),
    review: translate(
      'pages:claims.alerts.reviewDescription',
      'OCR confidence is zero. Check against the source document.'
    ),
    query: translate(
      'pages:claims.alerts.queryDescription',
      'View the response to the health authority query.'
    ),
  };
  const queryColor =
    colorScheme === 'dark'
      ? 'var(--mantine-color-orange-5)'
      : 'var(--mantine-color-orange-8)';
  const getAlertColor = (type: AlertType): string => {
    if (type === 'invalid' || type === 'review') {
      return 'var(--mantine-color-red-6)';
    }
    if (type === 'action') {
      return needsResponse
        ? 'var(--mantine-color-red-filled)'
        : 'var(--mantine-color-dimmed)';
    }
    return queryColor;
  };
  const icon = (type: AlertType, size: number, inMark = false) => {
    switch (type) {
      case 'invalid':
        return (
          <IconExclamationMark
            size={size}
            color={inMark ? 'white' : 'red'}
            aria-hidden
          />
        );
      case 'action': {
        if (!needsResponse) {
          return (
            <IconArrowUpRight
              size={size}
              stroke={1.5}
              color={inMark ? 'white' : 'var(--mantine-color-dimmed)'}
              aria-hidden
            />
          );
        }
        return (
          <IconAlertTriangle
            size={size}
            color={inMark ? 'white' : 'var(--mantine-color-red-filled)'}
            aria-hidden
          />
        );
      }
      case 'review':
        return (
          <IconEyeEdit
            size={size}
            color={inMark ? 'white' : 'red'}
            aria-hidden
          />
        );
      case 'query':
        return (
          <IconMessageReply
            size={size}
            color={inMark ? 'white' : queryColor}
            aria-hidden
          />
        );
    }
  };
  const renderMark = (type: AlertType) => (
    <ClaimAlertMark color={getAlertColor(type)}>
      {icon(type, 14, true)}
    </ClaimAlertMark>
  );
  const queryURL = `/queries?claimID=${encodeURIComponent(claimID)}`;
  const stopRowEvent = (event: SyntheticEvent) => event.stopPropagation();
  const soloStyle: CSSProperties = {
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    flexShrink: 0,
    border: '1px solid var(--mantine-color-default-border)',
    borderRadius: '50%',
    padding: 0,
  };

  if (alertTypes.length === 1) {
    const type = alertTypes[0];
    if (type === 'action') {
      return (
        <ClaimActionBadge
          requests={actionRequests}
          isProvider={isProvider}
          onOpen={onOpenActions}
        />
      );
    }
    if (type === 'query' && canViewQueries) {
      return (
        <Tooltip label={queryLabel} withArrow position="top">
          <Anchor
            component={Link}
            to={queryURL}
            target={QUERIES_WINDOW_TARGET}
            onClick={stopRowEvent}
            onDoubleClick={stopRowEvent}
            aria-label={queryLabel}
            data-testid="query-response-received"
            style={{ ...soloStyle, borderColor: queryColor }}
          >
            {renderMark(type)}
          </Anchor>
        </Tooltip>
      );
    }
    const testIDs: Partial<Record<AlertType, string>> = {
      review: 'manual-review-required',
      query: 'query-response-received',
    };
    const alertMark = renderMark(type);
    if (!canEdit || type === 'query') {
      return (
        <Tooltip label={labels[type]} withArrow position="top">
          <span
            role="img"
            aria-label={labels[type]}
            data-testid={testIDs[type]}
            style={{ ...soloStyle, borderColor: getAlertColor(type) }}
            onClick={stopRowEvent}
            onDoubleClick={stopRowEvent}
          >
            {alertMark}
          </span>
        </Tooltip>
      );
    }
    return (
      <Tooltip label={labels[type]} withArrow position="top">
        <UnstyledButton
          type="button"
          aria-label={labels[type]}
          data-testid={testIDs[type]}
          style={{ ...soloStyle, borderColor: getAlertColor(type) }}
          onClick={(event) => {
            event.stopPropagation();
            onEdit();
          }}
          onDoubleClick={stopRowEvent}
        >
          {alertMark}
        </UnstyledButton>
      </Tooltip>
    );
  }

  const first = alertTypes[0];
  const firstColor = getAlertColor(first);

  return (
    <Menu
      trigger="click-hover"
      openDelay={100}
      closeDelay={150}
      position="bottom-start"
      shadow="md"
      withinPortal
      width={280}
    >
      <Menu.Target>
        <UnstyledButton
          type="button"
          aria-label={translate('pages:claims.alerts.summary', 'Claim alerts')}
          onClick={stopRowEvent}
          onDoubleClick={stopRowEvent}
          style={{
            boxSizing: 'border-box',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 4,
            width: 'fit-content',
            minWidth: 46,
            maxWidth: 54,
            height: 24,
            paddingBlock: 2,
            paddingInlineStart: 2,
            paddingInlineEnd: 5,
            border: `1px solid ${firstColor}`,
            borderRadius: 999,
            color: firstColor,
            backgroundColor: `color-mix(in srgb, ${firstColor} 16%, transparent)`,
            flexShrink: 0,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {renderMark(first)}
          <span>+{alertTypes.length - 1}</span>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown onClick={stopRowEvent} onDoubleClick={stopRowEvent}>
        {alertTypes.map((type) => {
          const contents = (
            <>
              <Text size="sm" fw={500}>
                {labels[type]}
              </Text>
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'normal' }}>
                {descriptions[type]}
              </Text>
            </>
          );
          const itemProps = {
            leftSection: icon(type, 17),
          };
          if (type === 'query' && canViewQueries) {
            return (
              <Menu.Item
                key={type}
                {...itemProps}
                component={Link}
                to={queryURL}
                target={QUERIES_WINDOW_TARGET}
                onClick={stopRowEvent}
              >
                {contents}
              </Menu.Item>
            );
          }
          const action = type === 'action' ? onOpenActions : onEdit;
          const actionable = type === 'action' || (canEdit && type !== 'query');
          return (
            <Menu.Item
              key={type}
              {...itemProps}
              disabled={!actionable}
              onClick={(event) => {
                event.stopPropagation();
                if (actionable) action();
              }}
            >
              {contents}
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
};
